import { QueryClient } from "@tanstack/react-query";
import {
  ReviewPhoto,
  VenueAccessibilityExperienceStats,
  VenueReview,
} from "../../../types";
import type { VenueDetailsBySlugResponse } from "./useVenueDetailsBySlug";

/**
 * DEV-544/545 — client-side patches to the cached venue-details reviews, for edit and
 * delete of a user's own experience.
 *
 * Why patch rather than refetch: both writes dispatch the backend's `ReviewSaved`
 * event, which rebuilds the business cache *asynchronously*. A refetch issued straight
 * after the write returns the pre-write list, so the card would reappear (or revert)
 * for a few seconds. The mutation hooks still invalidate on a delay as a backstop; this
 * is what makes the change visible immediately.
 *
 * Every cached entry is patched, not just the active one: the venue-details query key
 * carries coordinates and tokens (@see venueDetailsQueryOptions), so the same venue can
 * sit in the cache under several keys and a stale sibling would resurface on navigation.
 */

const VENUE_DETAILS_KEY = ["loadVenueDetailsBySlug"];

/**
 * Review ids arrive from three directions — the API (`mapVenueReviews` passes `id`
 * through untouched), mutation variables typed as strings, and route params — so match
 * on the numeric value rather than trusting every one of them to be a number. A type
 * mismatch here fails silently: the patch simply finds nothing and the card never moves.
 */
const sameReview = (a: unknown, b: unknown) => Number(a) === Number(b);

function recomputeStats(
  coolCount: number,
  weirdCount: number,
): VenueAccessibilityExperienceStats {
  const totalCount = coolCount + weirdCount;
  return {
    coolCount,
    weirdCount,
    totalCount,
    coolPercentage: totalCount ? (coolCount / totalCount) * 100 : 0,
    weirdPercentage: totalCount ? (weirdCount / totalCount) * 100 : 0,
  };
}

/** Applies `delta` (+1/-1) to the Sweet or Sour tally for `experience`. */
function adjustStats(
  stats: VenueAccessibilityExperienceStats | undefined,
  experience: string | null | undefined,
  delta: number,
): VenueAccessibilityExperienceStats | undefined {
  // Nothing cached to adjust — the consumer falls back to counting the list itself.
  if (!stats) return stats;

  const cool = experience === "cool" ? stats.coolCount + delta : stats.coolCount;
  const weird =
    experience === "weird" ? stats.weirdCount + delta : stats.weirdCount;

  return recomputeStats(Math.max(0, cool), Math.max(0, weird));
}

function patchVenueCaches(
  queryClient: QueryClient,
  patch: (reviews: VenueReview[]) => {
    reviews: VenueReview[];
    stats: (
      current: VenueAccessibilityExperienceStats | undefined,
    ) => VenueAccessibilityExperienceStats | undefined;
  } | null,
) {
  queryClient.setQueriesData<VenueDetailsBySlugResponse>(
    { queryKey: VENUE_DETAILS_KEY },
    (previous) => {
      if (!previous?.details?.businessReviews) return previous;

      const result = patch(previous.details.businessReviews);
      // Identical reference: this cache entry holds a different venue, so nothing
      // re-renders off the back of it.
      if (!result) return previous;

      return {
        ...previous,
        details: {
          ...previous.details,
          businessReviews: result.reviews,
          venueAccessibilityExperienceStats: result.stats(
            previous.details.venueAccessibilityExperienceStats,
          ),
        },
      };
    },
  );
}

/**
 * Drops a deleted experience from every cached copy of its venue and decrements the
 * matching Sweet/Sour tally.
 */
export function removeReviewFromVenueCache(
  queryClient: QueryClient,
  reviewId: number | string,
) {
  patchVenueCaches(queryClient, (reviews) => {
    const removed = reviews.find((review) => sameReview(review.id, reviewId));
    if (!removed) return null;

    return {
      reviews: reviews.filter((review) => !sameReview(review.id, reviewId)),
      stats: (current) =>
        adjustStats(current, removed.accessibility_experience, -1),
    };
  });
}

/**
 * Writes an edit back into every cached copy of its venue: notes, Sweet/Sour rating and
 * the authoritative photo list `edit-review` returned. A flipped rating moves one count
 * from Sweet to Sour (or back).
 */
export function applyReviewEditToVenueCache(
  queryClient: QueryClient,
  edit: {
    reviewId: number | string;
    notes: string;
    accessibilityExperience: string | null;
    reviewPhotos: ReviewPhoto[];
  },
) {
  patchVenueCaches(queryClient, (reviews) => {
    const previous = reviews.find((review) => sameReview(review.id, edit.reviewId));
    if (!previous) return null;

    const updatedAt = new Date().toISOString();
    const ratingChanged =
      previous.accessibility_experience !== edit.accessibilityExperience;

    return {
      reviews: reviews.map((review) =>
        sameReview(review.id, edit.reviewId)
          ? {
              ...review,
              notes: edit.notes,
              accessibility_experience: edit.accessibilityExperience,
              reviewPhotos: edit.reviewPhotos,
              // The list sorts on updatedAt, so an edit moves the card the way the
              // backend would have on the next cache rebuild.
              updatedAt,
            }
          : review,
      ),
      stats: (current) => {
        if (!ratingChanged) return current;
        return adjustStats(
          adjustStats(current, previous.accessibility_experience, -1),
          edit.accessibilityExperience,
          1,
        );
      },
    };
  });
}
